import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormField } from '../ui/FormField';
import { DatePickerField } from '../ui/DatePickerField';
import { validators } from '../../helpers/validators';
import { useFormValidation } from '../../hooks/useFormValidation';
import { FormErrorSummary } from '../ui/FormErrorSummary';
import { StatusMessage } from '../ui/StatusMessage';
import { useSiteTree } from '../../hooks/useSiteTree';
import { getUniqueAlarmPaths, listSites, type UniqueAlarmPathItem } from '../../services/siteManagement/siteManagement.service';
import type { SiteTreeNode } from '../../services/siteManagement/siteManagement.types';
import { listCategories } from '../../services/category/category.service';
import type { CategorySummary } from '../../services/category/category.types';
import { OptionsSelectInput } from '../inputs/OptionsSelectInput';
import { lookupUsers } from '../../services/userManagement/userManagement.service';
import type { UserLookupItem } from '../../services/userManagement/userManagement.types';

let cachedCategories: CategorySummary[] | null = null;
let cachedSites: Array<{ sirId: number; title: string }> | null = null;
let cachedUsers: UserLookupItem[] | null = null;

interface ReportFormProps {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  messageFilter: string;
  monitoredOnly: boolean;
  groupAlarms: boolean;
  priority: number | null;
  minPriority: number;
  maxPriority: number;
  siteIds: number[];
  categoryIds: number[];
  alarmPathIds: number[];
  alarmStateIds?: number[];
  acknowledgedBy: string;
  includeStatistics: boolean;
  pageSize: number;
  onStartDateChange: (value: string) => void;
  onStartTimeChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  onEndTimeChange: (value: string) => void;
  onMessageFilterChange: (value: string) => void;
  onMonitoredOnlyChange: (value: boolean) => void;
  onGroupAlarmsChange: (value: boolean) => void;
  onPriorityChange: (value: number | null) => void;
  onMinPriorityChange: (value: number) => void;
  onMaxPriorityChange: (value: number) => void;
  onSiteIdsChange: (value: number[]) => void;
  onCategoryIdsChange: (value: number[]) => void;
  onAlarmPathIdsChange: (value: number[]) => void;
  onAlarmStateIdsChange?: (value: number[]) => void;
  onAcknowledgedByChange: (value: string) => void;
  onIncludeStatisticsChange: (value: boolean) => void;
  onPageSizeChange: (value: number) => void;
  showErrorSummary?: boolean;
  onCloseErrorSummary?: () => void;
  onValidate?: (isValid: boolean, errors: Partial<Record<string, string>>[]) => void;
}

function collectUniquePaths(
  paths: UniqueAlarmPathItem[],
  seenIds: Set<number>,
  result: UniqueAlarmPathItem[]
): void {
  for (const path of paths) {
    if (!seenIds.has(path.id)) {
      seenIds.add(path.id);
      result.push(path);
    }
  }
}

function mergeUniqueAlarmPaths(
  responses: Array<{ success?: boolean; data?: UniqueAlarmPathItem[] | null } | null | undefined>
): UniqueAlarmPathItem[] {
  const result: UniqueAlarmPathItem[] = [];
  const seenIds = new Set<number>();
  for (const res of responses) {
    if (res?.success && res.data) {
      collectUniquePaths(res.data, seenIds, result);
    }
  }
  return result;
}

type PriorityMode = 'specific' | 'range';

function getInitialPriorityMode(priority: number | null): PriorityMode {
  if (priority === null) {
    return 'range';
  }
  return 'specific';
}

function flattenSiteNodes(nodes: SiteTreeNode[] | null): Array<{ sirId: number; title: string }> {
  if (!nodes) {
    return [];
  }
  const sites: Array<{ sirId: number; title: string }> = [];
  nodes.forEach(node => {
    if (node.type === 'customer' && node.children) {
      node.children.forEach(child => {
        const sid = Number(child.sirId);
        if (child.type === 'site' && sid && sid > 0 && child.title) {
          sites.push({ sirId: sid, title: child.title });
        }
      });
    }
  });
  return sites;
}

const MAX_DATE_RANGE_DAYS = 365;

function createStartDateValidator(endDateValue: string) {
  return (value: unknown): string | null => {
    if (!endDateValue || value == null) {
      return null;
    }
    let valueStr: string | null;
    if (typeof value === 'string') {
      valueStr = value;
    } else if (value instanceof Date && !Number.isNaN(value.getTime())) {
      valueStr = value.toISOString();
    } else {
      return null;
    }
    const start = new Date(valueStr);
    const end = new Date(endDateValue);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return null;
    }
    const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    if (daysDiff > MAX_DATE_RANGE_DAYS) {
      return `Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days`;
    }
    if (start > end) {
      return 'Start date must be before end date';
    }
    return null;
  };
}

function createEndDateValidator(startDateValue: string) {
  return (value: unknown): string | null => {
    if (!startDateValue || value == null) {
      return null;
    }
    let valueStr: string | null;
    if (typeof value === 'string') {
      valueStr = value;
    } else if (value instanceof Date && !Number.isNaN(value.getTime())) {
      valueStr = value.toISOString();
    } else {
      return null;
    }
    const start = new Date(startDateValue);
    const end = new Date(valueStr);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return null;
    }
    const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    if (daysDiff > MAX_DATE_RANGE_DAYS) {
      return `Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days`;
    }
    if (end < start) {
      return 'End date must be after start date';
    }
    return null;
  };
}

function getSiteLabel(site: { sirId: number; title: string } | undefined, id: number): string {
  if (site) {
    return site.title;
  }
  return `Site ${id}`;
}

function getAlarmPathLabel(path: UniqueAlarmPathItem | undefined, id: number): string {
  if (path) {
    return path.pathToAlarm;
  }
  return `Path ${id}`;
}

function getSitePlaceholder(siteCount: number): string {
  if (siteCount === 0) {
    return 'Select site(s)\u2026';
  }
  return 'Click to add more\u2026';
}

function getCategoryPlaceholder(categoryCount: number): string {
  if (categoryCount === 0) {
    return 'Select category(ies)\u2026';
  }
  return 'Click to add more\u2026';
}

function buildCategorySiteMap(categories: CategorySummary[]): Record<number, Set<number>> {
  const map: Record<number, Set<number>> = {};
  categories.forEach(category => {
    const siteIds = (category.associatedSites ?? [])
      .map(site => Number(site.siteIdentifierRegistryId))
      .filter(siteId => !Number.isNaN(siteId) && siteId > 0);
    map[category.id] = new Set(siteIds);
  });
  return map;
}

function getReportPathPlaceholder(siteCount: number, pathCount: number): string {
  if (siteCount === 0) {
    return 'Select site(s) first';
  }
  if (pathCount === 0) {
    return 'Select path(s)\u2026';
  }
  return 'Click to add more\u2026';
}

function getEmptyPathMessage(totalPaths: number): string {
  if (totalPaths === 0) {
    return 'No paths available';
  }
  return 'No paths found';
}

function getUserDisplayName(user: UserLookupItem): string {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  const name = user.displayName?.trim() || fullName || 'Unknown User';
  if (!user.employeeId) {
    return name;
  }
  return `${name} - ${user.employeeId}`;
}

function computeHasErrors(errors: unknown): boolean {
  if (!Array.isArray(errors)) {
    return false;
  }
  return errors.some(v => !!(v && typeof v === 'object' && Object.keys(v as object).length > 0));
}

interface CategorySelectorProps {
  readonly categories: CategorySummary[];
  readonly categoryIds: number[];
  readonly categorySearch: string;
  readonly openCategoryDropdown: boolean;
  readonly onToggleCategoryDropdown: () => void;
  readonly onCategoryDropdownKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
  readonly onCategorySearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readonly onCategorySearchClick: (e: React.MouseEvent<HTMLInputElement>) => void;
  readonly onRemoveCategoryChip: (e: React.MouseEvent<HTMLElement>) => void;
  readonly onToggleCategory: (e: React.MouseEvent<HTMLButtonElement>) => void;
  readonly categoryWrapperRef: React.RefObject<HTMLDivElement | null>;
}

interface SiteSelectorProps {
  readonly allSites: Array<{ sirId: number; title: string }>;
  readonly siteIds: number[];
  readonly siteSearch: string;
  readonly openSiteDropdown: boolean;
  readonly onToggleSiteDropdown: () => void;
  readonly onSiteDropdownKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
  readonly onSiteSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readonly onSiteSearchClick: (e: React.MouseEvent<HTMLInputElement>) => void;
  readonly onRemoveSiteChip: (e: React.MouseEvent<HTMLElement>) => void;
  readonly onToggleSite: (e: React.MouseEvent<HTMLButtonElement>) => void;
  readonly siteWrapperRef: React.RefObject<HTMLDivElement | null>;
}

interface AlarmPathSelectorProps {
  readonly paths: UniqueAlarmPathItem[];
  readonly alarmPathIds: number[];
  readonly siteIds: number[];
  readonly pathSearch: string;
  readonly openPathDropdown: boolean;
  readonly onTogglePathDropdown: () => void;
  readonly onPathDropdownKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
  readonly onPathSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readonly onPathSearchClick: (e: React.MouseEvent<HTMLInputElement>) => void;
  readonly onRemovePathChip: (e: React.MouseEvent<HTMLElement>) => void;
  readonly onTogglePath: (e: React.MouseEvent<HTMLButtonElement>) => void;
  readonly pathWrapperRef: React.RefObject<HTMLDivElement | null>;
}

interface PrioritySectionProps {
  readonly priorityMode: PriorityMode;
  readonly priority: number | null;
  readonly minPriority: number;
  readonly maxPriority: number;
  readonly minPriorityError?: string;
  readonly maxPriorityError?: string;
  readonly onSpecificModeChange: () => void;
  readonly onRangeModeChange: () => void;
  readonly onPriorityInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readonly onMinPriorityChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readonly onMaxPriorityChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

function CategorySelector({
  categories,
  categoryIds,
  categorySearch,
  openCategoryDropdown,
  onToggleCategoryDropdown,
  onCategoryDropdownKeyDown,
  onCategorySearchChange,
  onCategorySearchClick,
  onRemoveCategoryChip,
  onToggleCategory,
  categoryWrapperRef,
}: CategorySelectorProps) {
  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(categorySearch.toLowerCase())
  );

  return (
    <div className="form__group">
      <label htmlFor="category" className="form__label">
        Categories
      </label>
      <div className="form-field form-field--responsive" ref={categoryWrapperRef}>
        <div className="select2-like">
          <div className="select2-input">
            {categoryIds.map(id => {
              const category = categories.find(c => c.id === id);
              const label = category ? category.name : `Category ${id}`;
              return (
                <span key={`category-${id}`} className="chip">
                  <span className="chip__label">{label}</span>
                  <button
                    type="button"
                    className="chip__remove"
                    aria-label="remove"
                    data-category-id={String(id)}
                    onClick={onRemoveCategoryChip}
                  />
                </span>
              );
            })}
            <button
              type="button"
              className="select2-input__placeholder"
              aria-haspopup="listbox"
              aria-expanded={openCategoryDropdown}
              onClick={onToggleCategoryDropdown}
              onKeyDown={onCategoryDropdownKeyDown}
            >
              {getCategoryPlaceholder(categoryIds.length)}
            </button>
          </div>
          {openCategoryDropdown && (
            <div className="select2-dropdown">
              <input
                type="text"
                className="select2-dropdown__search"
                placeholder="Search categories..."
                value={categorySearch}
                onChange={onCategorySearchChange}
                onClick={onCategorySearchClick}
              />
              <div className="select2-dropdown__list">
                {filteredCategories.map(category => {
                  const isSelected = categoryIds.includes(category.id);
                  let itemClassName = 'select2-dropdown__item';
                  if (isSelected) {
                    itemClassName += ' selected';
                  }
                  return (
                    <button
                      key={category.id}
                      type="button"
                      className={itemClassName}
                      aria-pressed={isSelected}
                      data-category-id={String(category.id)}
                      onClick={onToggleCategory}
                    >
                      <span className="select2-list-item__label">{category.name}</span>
                      <span className="select2-list-item__badge">
                        {isSelected && <span className="se-icon--acknowledged" aria-hidden="true"></span>}
                      </span>
                    </button>
                  );
                })}
                {filteredCategories.length === 0 && (
                  <div className="select2-dropdown__item select2-dropdown__item--empty">
                    No categories found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {categoryIds.length > 0 && (
        <small className="text-muted">{categoryIds.length} category(ies) selected</small>
      )}
    </div>
  );
}

function SiteSelector({
  allSites,
  siteIds,
  siteSearch,
  openSiteDropdown,
  onToggleSiteDropdown,
  onSiteDropdownKeyDown,
  onSiteSearchChange,
  onSiteSearchClick,
  onRemoveSiteChip,
  onToggleSite,
  siteWrapperRef,
}: SiteSelectorProps) {
  const filteredSites = allSites.filter(site =>
    site.title.toLowerCase().includes(siteSearch.toLowerCase())
  );

  return (
    <div className="form__group">
      <label htmlFor="site" className="form__label">
        Sites
      </label>
      <div className="form-field form-field--responsive" ref={siteWrapperRef}>
        <div className="select2-like">
          <div className="select2-input">
            {siteIds.map(id => {
              const site = allSites.find(s => s.sirId === id);
              const label = getSiteLabel(site, id);
              return (
                <span key={`site-${id}`} className="chip">
                  <span className="chip__label">{label}</span>
                  <button
                    type="button"
                    className="chip__remove"
                    aria-label="remove"
                    data-site-id={String(id)}
                    onClick={onRemoveSiteChip}
                  />
                </span>
              );
            })}
            <button
              type="button"
              className="select2-input__placeholder"
              aria-haspopup="listbox"
              aria-expanded={openSiteDropdown}
              onClick={onToggleSiteDropdown}
              onKeyDown={onSiteDropdownKeyDown}
            >
              {getSitePlaceholder(siteIds.length)}
            </button>
          </div>
          {openSiteDropdown && (
            <div className="select2-dropdown">
              <input
                type="text"
                className="select2-dropdown__search"
                placeholder="Search sites..."
                value={siteSearch}
                onChange={onSiteSearchChange}
                onClick={onSiteSearchClick}
              />
              <div className="select2-dropdown__list">
                {filteredSites.map(site => {
                  const isSelected = siteIds.includes(site.sirId);
                  let itemClassName = 'select2-dropdown__item';
                  if (isSelected) {
                    itemClassName += ' selected';
                  }
                  return (
                    <button
                      key={site.sirId}
                      type="button"
                      className={itemClassName}
                      aria-pressed={isSelected}
                      data-site-id={String(site.sirId)}
                      onClick={onToggleSite}
                    >
                      <span className="select2-list-item__label">{site.title}</span>
                      <span className="select2-list-item__badge">
                        {isSelected && <span className="se-icon--acknowledged" aria-hidden="true"></span>}
                      </span>
                    </button>
                  );
                })}
                {filteredSites.length === 0 && (
                  <div className="select2-dropdown__item select2-dropdown__item--empty">
                    No sites found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {siteIds.length > 0 && (
        <small className="text-muted">{siteIds.length} site(s) selected</small>
      )}
    </div>
  );
}

function AlarmPathSelector({
  paths,
  alarmPathIds,
  siteIds,
  pathSearch,
  openPathDropdown,
  onTogglePathDropdown,
  onPathDropdownKeyDown,
  onPathSearchChange,
  onPathSearchClick,
  onRemovePathChip,
  onTogglePath,
  pathWrapperRef,
}: AlarmPathSelectorProps) {
  const filteredPaths = paths.filter(path =>
    path.pathToAlarm?.toLowerCase().includes(pathSearch.toLowerCase())
  );
  const pathsDisabled = !siteIds || siteIds.length === 0;

  return (
    <div className="form__group">
      <label htmlFor="alarmPaths" className="form__label">
        Alarm Paths
      </label>
      <div className="form-field form-field--responsive" ref={pathWrapperRef}>
        <div className="select2-like">
          <div className={`select2-input${pathsDisabled ? ' select2-input--disabled' : ''}`}>
            {alarmPathIds.map(id => {
              const path = paths.find(p => p.id === id);
              const label = getAlarmPathLabel(path, id);
              return (
                <span key={`path-${id}`} className="chip">
                  <span className='chip__label'>{label}</span>
                  <button
                    type="button"
                    className="chip__remove"
                    aria-label="remove"
                    data-path-id={String(id)}
                    onClick={onRemovePathChip}
                  />
                </span>
              );
            })}
            <button
              type="button"
              className="select2-input__placeholder"
              aria-haspopup="listbox"
              aria-expanded={openPathDropdown}
              onClick={onTogglePathDropdown}
              onKeyDown={onPathDropdownKeyDown}
            >
              {getReportPathPlaceholder(siteIds.length, alarmPathIds.length)}
            </button>
          </div>
          {openPathDropdown && !pathsDisabled && (
            <div className="select2-dropdown">
              <input
                type="text"
                className="select2-dropdown__search"
                placeholder="Search paths..."
                value={pathSearch}
                onChange={onPathSearchChange}
                onClick={onPathSearchClick}
              />
              <div className="select2-dropdown__list">
                {filteredPaths.map(path => {
                  const isSelected = alarmPathIds.includes(path.id);
                  let itemClassName = 'select2-dropdown__item';
                  if (isSelected) {
                    itemClassName += ' selected';
                  }
                  return (
                    <button
                      key={path.id}
                      type="button"
                      className={itemClassName}
                      aria-pressed={isSelected}
                      data-path-id={String(path.id)}
                      onClick={onTogglePath}
                    >
                      <span className="select2-list-item__label">{path.pathToAlarm}</span>
                      <span className="select2-list-item__badge">
                        {isSelected && <span className="se-icon--acknowledged" aria-hidden="true"></span>}
                      </span>
                    </button>
                  );
                })}
                {filteredPaths.length === 0 && (
                  <div className="select2-dropdown__item select2-dropdown__item--empty">
                    {getEmptyPathMessage(paths.length)}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {alarmPathIds.length > 0 && (
        <small className="text-muted">{alarmPathIds.length} path(s) selected</small>
      )}
    </div>
  );
}

function PrioritySection({
  priorityMode,
  priority,
  minPriority,
  maxPriority,
  minPriorityError,
  maxPriorityError,
  onSpecificModeChange,
  onRangeModeChange,
  onPriorityInputChange,
  onMinPriorityChange,
  onMaxPriorityChange,
}: PrioritySectionProps) {
  const specificModeActive = priorityMode === 'specific';
  const showMinMaxDisabledMsg = priority !== null;
  const showSpecificDisabledMsg = !specificModeActive;

  return (
    <>
      <div className="form__group">
        <h4 className="form__subtitle">Priority</h4>
        <div className="form__row mb-1">
          <label className="form__label">
            <input
              type="radio"
              name="priorityMode"
              checked={specificModeActive}
              onChange={onSpecificModeChange}
            />
            {' '}
            Specific
          </label>
          <label className="form__label">
            <input
              type="radio"
              name="priorityMode"
              checked={!specificModeActive}
              onChange={onRangeModeChange}
            />
            {' '}
            Range
          </label>
        </div>

        <div className="form-field form-field--responsive">
          <input
            id="priority"
            name="priority"
            type="number"
            className="form-field__input"
            placeholder="e.g., 5"
            value={priority ?? ''}
            onChange={onPriorityInputChange}
            disabled={!specificModeActive}
          />
        </div>
        {showSpecificDisabledMsg && (
          <small className="text-muted">Disabled when using priority range</small>
        )}
      </div>

      <div className="form__group">
        <span className="form__label">Priority Range</span>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <FormField
              label="Min"
              name="minPriority"
              type="number"
              value={String(minPriority)}
              showErrorMessage={false}
              placeholder="0"
              onChange={onMinPriorityChange}
              error={minPriorityError}
              disabled={specificModeActive}
            />
          </div>
          <div style={{ flex: 1 }}>
            <FormField
              label="Max"
              name="maxPriority"
              type="number"
              value={String(maxPriority)}
              showErrorMessage={false}
              placeholder="10000"
              onChange={onMaxPriorityChange}
              error={maxPriorityError}
              disabled={specificModeActive}
            />
          </div>
        </div>
        {showMinMaxDisabledMsg && (
          <small className="text-muted">Disabled when using specific priority</small>
        )}
      </div>
    </>
  );
}

const ReportForm: React.FC<ReportFormProps> = ({
  startDate,
  startTime,
  endDate,
  endTime,
  messageFilter,
  monitoredOnly,
  groupAlarms,
  priority,
  minPriority,
  maxPriority,
  siteIds,
  categoryIds,
  alarmPathIds,
  alarmStateIds = [],
  acknowledgedBy,
  includeStatistics,
  pageSize,
  onStartDateChange,
  onStartTimeChange,
  onEndDateChange,
  onEndTimeChange,
  onMessageFilterChange,
  onMonitoredOnlyChange,
  onGroupAlarmsChange,
  onPriorityChange,
  onMinPriorityChange,
  onMaxPriorityChange,
  onSiteIdsChange,
  onCategoryIdsChange,
  onAlarmPathIdsChange,
  onAlarmStateIdsChange,
  onAcknowledgedByChange,
  onIncludeStatisticsChange,
  onPageSizeChange,
  showErrorSummary = false,
  onCloseErrorSummary,
  onValidate,
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const { nodes: siteNodes } = useSiteTree();
  const [paths, setPaths] = useState<UniqueAlarmPathItem[]>([]);
  const [siteLookupVersion, setSiteLookupVersion] = useState(0);
  const [categories, setCategories] = useState<CategorySummary[]>(cachedCategories ?? []);
  const [users, setUsers] = useState<UserLookupItem[]>(cachedUsers ?? []);
  const [categorySearch, setCategorySearch] = useState('');
  const [openCategoryDropdown, setOpenCategoryDropdown] = useState(false);
  const categoryWrapperRef = useRef<HTMLDivElement | null>(null);
  const [siteSearch, setSiteSearch] = useState('');
  const [pathSearch, setPathSearch] = useState('');
  const [openSiteDropdown, setOpenSiteDropdown] = useState(false);
  const [openPathDropdown, setOpenPathDropdown] = useState(false);
  const [priorityMode, setPriorityMode] = useState<PriorityMode>(getInitialPriorityMode(priority));
  const siteWrapperRef = useRef<HTMLDivElement | null>(null);
  const pathWrapperRef = useRef<HTMLDivElement | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Flatten site tree to get all sites from all customers
  const treeSites = React.useMemo(() => flattenSiteNodes(siteNodes), [siteNodes]);
  const allSites = React.useMemo(() => {
    const sitesById = new Map<number, { sirId: number; title: string }>();
    for (const site of treeSites) {
      sitesById.set(site.sirId, site);
    }
    for (const site of cachedSites ?? []) {
      sitesById.set(site.sirId, site);
    }
    return [...sitesById.values()];
  }, [treeSites, cachedSites, siteLookupVersion]);

  const categorySiteMap = React.useMemo(() => buildCategorySiteMap(categories), [categories]);
  const filteredSitesByCategory = React.useMemo(() => {
    if (categoryIds.length === 0) {
      return allSites;
    }
    return allSites.filter(site =>
      categoryIds.some(catId => categorySiteMap[catId]?.has(site.sirId))
    );
  }, [allSites, categoryIds, categorySiteMap]);

  useEffect(() => {
    let mounted = true;
    listCategories()
      .then(res => {
        if (mounted && res?.success && res.data) {
          cachedCategories = res.data;
          setCategories(res.data);
        }
      })
      .catch(err => {
        console.info('[ReportForm] listCategories error', err);
      });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    lookupUsers()
      .then(res => {
        if (mounted && res?.success && res.data) {
          cachedUsers = res.data;
          setUsers(res.data);
        }
      })
      .catch(err => {
        console.info('[ReportForm] lookupUsers error', err);
      });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    listSites()
      .then(res => {
        if (mounted && res?.success && res.data) {
          cachedSites = res.data
            .filter(site => Number(site.id) > 0)
            .map(site => ({
              sirId: Number(site.id),
              title: site.siteName || site.displayName || site.siteIdentifier || `Site ${site.id}`,
            }));
          setSiteLookupVersion(version => version + 1);
        }
      })
      .catch(err => {
        console.info('[ReportForm] listSites error', err);
      });
    return () => { mounted = false; };
  }, []);

  // Load alarm paths when sites change
  useEffect(() => {
    if (!siteIds || siteIds.length === 0) {
      setPaths([]);
      onAlarmPathIdsChange([]);
      return undefined;
    }
    let mounted = true;
    // Load paths from all selected sites
    Promise.all(siteIds.map(id => getUniqueAlarmPaths(id)))
      .then(responses => {
        if (mounted) {
          setPaths(mergeUniqueAlarmPaths(responses));
        }
      })
      .catch(err => {
        console.info('[ReportForm] getUniqueAlarmPaths error', err);
        if (mounted) {
          setPaths([]);
        }
      });
    return () => { mounted = false; };
  }, [siteIds, onAlarmPathIdsChange]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryWrapperRef.current && !categoryWrapperRef.current.contains(e.target as Node)) {
        setOpenCategoryDropdown(false);
      }
      if (siteWrapperRef.current && !siteWrapperRef.current.contains(e.target as Node)) {
        setOpenSiteDropdown(false);
      }
      if (pathWrapperRef.current && !pathWrapperRef.current.contains(e.target as Node)) {
        setOpenPathDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { errors, handleChangeFormField, validateAll, setValues } = useFormValidation({
    initialValues: [{
      startDate,
      startTime,
      endDate,
      endTime,
      minPriority,
      maxPriority,
      pageSize,
    }],
    rules: {
      startDate: [
        validators.required('Start Date is required'),
        createStartDateValidator(endDate),
      ],
      startTime: [validators.required('Start Time is required')],
      endDate: [
        validators.required('End Date is required'),
        createEndDateValidator(startDate),
      ],
      endTime: [validators.required('End Time is required')],
      minPriority: [
        validators.minValue(0, 'Minimum Priority must be at least 0'),
        validators.maxValue(10000, 'Minimum Priority must be at most 10000'),
      ],
      maxPriority: [
        validators.minValue(0, 'Maximum Priority must be at least 0'),
        validators.maxValue(10000, 'Maximum Priority must be at most 10000'),
      ],
      pageSize: [
        validators.required('Page Size is required'),
        validators.minValue(1, 'Page Size must be at least 1'),
        validators.maxValue(10000, 'Page Size must be at most 10000'),
      ],
    },
  });

  // Expose validation function to parent
  useEffect(() => {
    if (onValidate) {
      const validationErrors = validateAll();
      onValidate(!computeHasErrors(validationErrors), validationErrors ?? []);
    }
  }, [startDate, startTime, endDate, endTime, minPriority, maxPriority, pageSize]);

  // Scroll to top when errors are shown
  useEffect(() => {
    if (showErrorSummary && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showErrorSummary]);

  const uniqueFormErrorSet = new Set(errors.flatMap(err => Object.values(err).filter(Boolean)));
  const uniqueFormErrors = [...uniqueFormErrorSet];

  // Form field change handlers
  const handleStartDateChange = useCallback((value: string) => {
    setTouched(prev => ({ ...prev, startDate: true }));
    setValues(prev => {
      const newValues = [...prev];
      newValues[0] = { ...newValues[0], startDate: value };
      return newValues;
    });
    onStartDateChange(value);
  }, [setValues, onStartDateChange]);

  const handleStartTimeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleChangeFormField(0, 'startTime')(e);
    onStartTimeChange(e.target.value);
  }, [handleChangeFormField, onStartTimeChange]);

  const handleEndDateChange = useCallback((value: string) => {
    setTouched(prev => ({ ...prev, endDate: true }));
    setValues(prev => {
      const newValues = [...prev];
      newValues[0] = { ...newValues[0], endDate: value };
      return newValues;
    });
    onEndDateChange(value);
  }, [setValues, onEndDateChange]);

  const handleEndTimeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleChangeFormField(0, 'endTime')(e);
    onEndTimeChange(e.target.value);
  }, [handleChangeFormField, onEndTimeChange]);

  const handleToggleCategoryDropdown = useCallback(() => {
    setOpenCategoryDropdown(prev => !prev);
  }, []);

  const handleCategoryDropdownKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpenCategoryDropdown(prev => !prev);
    }
  }, []);

  const handleCategorySearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCategorySearch(e.target.value);
  }, []);

  const handleCategorySearchClick = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
  }, []);

  const handleRemoveCategoryChip = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    const id = Number((e.currentTarget as HTMLElement).dataset.categoryId);
    onCategoryIdsChange(categoryIds.filter(cid => cid !== id));
  }, [categoryIds, onCategoryIdsChange]);

  const handleToggleCategory = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const id = Number(e.currentTarget.dataset.categoryId);
    if (categoryIds.includes(id)) {
      onCategoryIdsChange(categoryIds.filter(cid => cid !== id));
    } else {
      onCategoryIdsChange([...categoryIds, id]);
    }
  }, [categoryIds, onCategoryIdsChange]);

  // Site dropdown handlers
  const handleToggleSiteDropdown = useCallback(() => {
    setOpenSiteDropdown(prev => !prev);
  }, []);

  const handleSiteDropdownKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpenSiteDropdown(prev => !prev);
    }
  }, []);

  const handleSiteSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSiteSearch(e.target.value);
  }, []);

  const handleSiteSearchClick = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
  }, []);

  const handleRemoveSiteChip = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    const id = Number((e.currentTarget as HTMLElement).dataset.siteId);
    onSiteIdsChange(siteIds.filter(sid => sid !== id));
  }, [siteIds, onSiteIdsChange]);

  const handleToggleSite = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const id = Number(e.currentTarget.dataset.siteId);
    if (siteIds.includes(id)) {
      onSiteIdsChange(siteIds.filter(sid => sid !== id));
    } else {
      onSiteIdsChange([...siteIds, id]);
    }
  }, [siteIds, onSiteIdsChange]);

  // Path dropdown handlers
  const handleTogglePathDropdown = useCallback(() => {
    if (!siteIds || siteIds.length === 0) {
      return;
    }
    setOpenPathDropdown(prev => !prev);
  }, [siteIds]);

  const handlePathDropdownKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!siteIds || siteIds.length === 0) {
        return;
      }
      setOpenPathDropdown(prev => !prev);
    }
  }, [siteIds]);

  const handlePathSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPathSearch(e.target.value);
  }, []);

  const handlePathSearchClick = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
  }, []);

  const handleRemovePathChip = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    const id = Number((e.currentTarget as HTMLElement).dataset.pathId);
    onAlarmPathIdsChange(alarmPathIds.filter(pid => pid !== id));
  }, [alarmPathIds, onAlarmPathIdsChange]);

  const handleTogglePath = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const id = Number(e.currentTarget.dataset.pathId);
    if (alarmPathIds.includes(id)) {
      onAlarmPathIdsChange(alarmPathIds.filter(pid => pid !== id));
    } else {
      onAlarmPathIdsChange([...alarmPathIds, id]);
    }
  }, [alarmPathIds, onAlarmPathIdsChange]);

  // Filter and options handlers
  const handleMessageFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onMessageFilterChange(e.target.value);
  }, [onMessageFilterChange]);

  const handleSpecificPriorityModeChange = useCallback(() => {
    setPriorityMode('specific');
    onMinPriorityChange(0);
    onMaxPriorityChange(10000);
  }, [onMinPriorityChange, onMaxPriorityChange]);

  const handleRangePriorityModeChange = useCallback(() => {
    setPriorityMode('range');
    onPriorityChange(null);
  }, [onPriorityChange]);

  const handlePriorityInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.trim();
    if (val === '') {
      onPriorityChange(null);
    } else {
      const num = Number.parseInt(val, 10);
      if (Number.isNaN(num)) {
        onPriorityChange(null);
      } else {
        onPriorityChange(num);
      }
    }
  }, [onPriorityChange]);

  const handleMinPriorityChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleChangeFormField(0, 'minPriority')(e);
    onMinPriorityChange(Number(e.target.value));
  }, [handleChangeFormField, onMinPriorityChange]);

  const handleMaxPriorityChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleChangeFormField(0, 'maxPriority')(e);
    onMaxPriorityChange(Number(e.target.value));
  }, [handleChangeFormField, onMaxPriorityChange]);

  const handlePageSizeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleChangeFormField(0, 'pageSize')(e);
    onPageSizeChange(Number(e.target.value));
  }, [handleChangeFormField, onPageSizeChange]);

  const handleIncludeStatisticsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onIncludeStatisticsChange(e.target.checked);
  }, [onIncludeStatisticsChange]);

  const handleMonitoredOnlyChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onMonitoredOnlyChange(e.target.checked);
  }, [onMonitoredOnlyChange]);

  const handleGroupAlarmsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onGroupAlarmsChange(e.target.checked);
  }, [onGroupAlarmsChange]);

  const alarmStateOptions = [
    { value: 0, label: 'Normal' },
    { value: 1, label: 'Active' },
    { value: 2, label: 'Acknowledged' },
    { value: 3, label: 'Reset' },
  ] as const;

  const handleAlarmStateToggle = useCallback((stateId: number) => {
    if (!onAlarmStateIdsChange) {
      return;
    }

    const currentValues = alarmStateIds ?? [];
    const isSelected = currentValues.includes(stateId);
    const nextValues = isSelected
      ? currentValues.filter(value => value !== stateId)
      : [...currentValues, stateId];

    onAlarmStateIdsChange(nextValues);
  }, [alarmStateIds, onAlarmStateIdsChange]);

  const acknowledgedByOptions = users.map(user => ({
    id: user.id,
    label: getUserDisplayName(user),
  }));

  const handleAcknowledgedByChange = useCallback((values: Array<string | number>) => {
    onAcknowledgedByChange(String(values.at(-1) ?? ''));
  }, [onAcknowledgedByChange]);

  return (
    <form ref={formRef} className="form">
      <h3 className="heading__title heading__title--reports">Generate Alarm Report</h3>

      {showErrorSummary && uniqueFormErrorSet.size > 0 && (
        <StatusMessage kind="error" onClose={onCloseErrorSummary} className='mt-2'>
          <FormErrorSummary errors={uniqueFormErrors} />
        </StatusMessage>
      )}

      <div className="form__row">
        <div className="form__group mt-2">
            <DatePickerField
            label="Start Date"
            name="startDate"
            value={startDate}
            showErrorMessage={false}
            onChange={handleStartDateChange}
            error={(touched.startDate || showErrorSummary) ? errors[0]?.startDate : undefined}
            />
        </div>

        <div className="form__group mt-2">
            <FormField
            label="Start Time"
            name="startTime"
            type="time"
            value={startTime}
            showErrorMessage={false}
            onChange={handleStartTimeChange}
            error={errors[0]?.startTime}
            />
        </div>
      </div>

      <div className="form__row">      
        <div className="form__group">
            <DatePickerField
            label="End Date"
            name="endDate"
            value={endDate}
            showErrorMessage={false}
            onChange={handleEndDateChange}
            error={(touched.endDate || showErrorSummary) ? errors[0]?.endDate : undefined}
            />
        </div>

        <div className="form__group">
            <FormField
              label="End Time"
              name="endTime"
              type="time"
              value={endTime}
              showErrorMessage={false}
              onChange={handleEndTimeChange}
              error={errors[0]?.endTime}
            />
        </div>
      </div>
      
        <CategorySelector
          categories={categories}
          categoryIds={categoryIds}
          categorySearch={categorySearch}
          openCategoryDropdown={openCategoryDropdown}
          onToggleCategoryDropdown={handleToggleCategoryDropdown}
          onCategoryDropdownKeyDown={handleCategoryDropdownKeyDown}
          onCategorySearchChange={handleCategorySearchChange}
          onCategorySearchClick={handleCategorySearchClick}
          onRemoveCategoryChip={handleRemoveCategoryChip}
          onToggleCategory={handleToggleCategory}
          categoryWrapperRef={categoryWrapperRef}
        />

        <SiteSelector
          allSites={filteredSitesByCategory}
          siteIds={siteIds}
          siteSearch={siteSearch}
          openSiteDropdown={openSiteDropdown}
          onToggleSiteDropdown={handleToggleSiteDropdown}
          onSiteDropdownKeyDown={handleSiteDropdownKeyDown}
          onSiteSearchChange={handleSiteSearchChange}
          onSiteSearchClick={handleSiteSearchClick}
          onRemoveSiteChip={handleRemoveSiteChip}
          onToggleSite={handleToggleSite}
          siteWrapperRef={siteWrapperRef}
        />
     
        <AlarmPathSelector
          paths={paths}
          alarmPathIds={alarmPathIds}
          siteIds={siteIds}
          pathSearch={pathSearch}
          openPathDropdown={openPathDropdown}
          onTogglePathDropdown={handleTogglePathDropdown}
          onPathDropdownKeyDown={handlePathDropdownKeyDown}
          onPathSearchChange={handlePathSearchChange}
          onPathSearchClick={handlePathSearchClick}
          onRemovePathChip={handleRemovePathChip}
          onTogglePath={handleTogglePath}
          pathWrapperRef={pathWrapperRef}
        />
     
  
      <div className="form__section">
        <h4 className="form__subtitle">Filters</h4>

        <div className="form__group">
          <label htmlFor="messageFilter" className="form__label">
            Message Filter
          </label>
          <div className="form-field form-field--responsive">
            <input
              id="messageFilter"
              name="messageFilter"
              type="text"
              className="form-field__input"
              placeholder="Filter by message content"
              value={messageFilter}
              onChange={handleMessageFilterChange}
            />
          </div>
        </div>

        <div className="form__row">
          <label className="form__label">
            <input
              type="checkbox"
              checked={monitoredOnly}
              onChange={handleMonitoredOnlyChange}
              style={{ marginRight: '8px' }}
            />{' '}
            Monitored Only
          </label>
          <label className="form__label">
            <input
              type="checkbox"
              checked={groupAlarms}
              onChange={handleGroupAlarmsChange}
              style={{ marginRight: '8px' }}
            />{' '}
            Group Alarms
          </label>
        </div>

        <div className="form__group">
          <h4 className="form__subtitle">Alarm States</h4>
          <div className='form__row mb-1'>
            {alarmStateOptions.map(option => {
              const checked = (alarmStateIds ?? []).includes(option.value);
              return (
                <label key={option.value} className="form__label" style={{ marginBottom: 0 }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => handleAlarmStateToggle(option.value)}
                    style={{ marginRight: '8px' }}
                  />
                  {option.label}
                </label>
              );
            })}
          </div>
        </div>

        <div className="form__group">
          <label className="form__label">Acknowledged By</label>
          <OptionsSelectInput
            options={acknowledgedByOptions}
            selected={acknowledgedBy ? [acknowledgedBy] : []}
            onChange={handleAcknowledgedByChange}
            placeholder="Search people..."
          />
        </div>

      </div>

      <PrioritySection
        priorityMode={priorityMode}
        priority={priority}
        minPriority={minPriority}
        maxPriority={maxPriority}
        minPriorityError={errors[0]?.minPriority}
        maxPriorityError={errors[0]?.maxPriority}
        onSpecificModeChange={handleSpecificPriorityModeChange}
        onRangeModeChange={handleRangePriorityModeChange}
        onPriorityInputChange={handlePriorityInputChange}
        onMinPriorityChange={handleMinPriorityChange}
        onMaxPriorityChange={handleMaxPriorityChange}
      />

      <div className="form__section">
        <h4 className="form__subtitle">Options</h4>

        <div className="form__group">
          <FormField
            label="Page Size"
            name="pageSize"
            type="number"
            value={String(pageSize)}
            showErrorMessage={false}
            onChange={handlePageSizeChange}
            error={errors[0]?.pageSize}
          />
        </div>

        <div className="form__group">
          <label className="form__label">
            <input
              type="checkbox"
              checked={includeStatistics}
              onChange={handleIncludeStatisticsChange}
              style={{ marginRight: '8px' }}
            />{' '}
            Include Statistics
          </label>
        </div>
      </div>
    </form>
  );
};

export default ReportForm;
